import { ref } from 'vue';
import type {
  GetProductPropertyArgs,
  GetProductPropertyResponse,
  ProductProperty,
} from '../types/product-property';

type UseLoadAllProductProperties = {
  onSuccess?: (data: GetProductPropertyResponse) => void;
};

export const useLoadAllProductProperties = (args?: UseLoadAllProductProperties) => {
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref(false);
  const productProperties = ref<ProductProperty[]>([]);

  const getProductPropertyArgs = ref<GetProductPropertyArgs>({
    limit: 150,
    offset: 0,
    orderBy: {
      attribute: 'CreatedAt',
      direction: 'ASC',
    },
  });

  const loadAll = async (
    loadProductPropertiesServiceCall: (
      args: GetProductPropertyArgs
    ) => Promise<GetProductPropertyResponse>
  ) => {
    try {
      error.value = false;
      loading.value = true;
      const { productProperties: firstProductProperties, totalCount } =
        await loadProductPropertiesServiceCall(getProductPropertyArgs.value);

      const remainingFetches = Math.ceil(
        (totalCount - firstProductProperties.length) / getProductPropertyArgs.value.limit
      );

      const promises = [];
      for (let i = 1; i <= remainingFetches; i++) {
        const newArgs: GetProductPropertyArgs = {
          ...getProductPropertyArgs.value,
          offset: i * getProductPropertyArgs.value.limit,
        };
        promises.push(loadProductPropertiesServiceCall(newArgs));
      }

      const results = await Promise.all(promises);
      const remainingProductProperties = results.flatMap(result => result.productProperties);
      productProperties.value = [...firstProductProperties, ...remainingProductProperties];
      loaded.value = true;

      if (args?.onSuccess) {
        args.onSuccess({ totalCount, productProperties: productProperties.value });
      }
    } catch (e) {
      error.value = true;
    } finally {
      loading.value = false;
    }
  };

  const reset = () => {
    loading.value = false;
    loaded.value = false;
    error.value = false;
    productProperties.value = [];
  };

  return {
    error,
    loading,
    loaded,
    productProperties,
    loadAll,
    reset,
  };
};
